import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Camera, MapPin, CheckCircle2, AlertTriangle, Loader2, RefreshCw, LogIn, LogOut } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

/**
 * PunchDialog — captures GPS + live camera photo, then submits to /me/attendance/punch.
 * Props:
 *   open, onOpenChange
 *   punchType: 'in' | 'out'
 *   onSuccess: () => void  (called after server confirms)
 *
 * Flow:
 *   1. Fetch attendance settings (so we know what's mandatory)
 *   2. Acquire GPS (geolocation API)
 *   3. Start camera stream, show live preview
 *   4. User clicks "Capture Photo" → frame drawn to canvas → base64
 *   5. Submit; show server-side validation errors clearly
 */
const PunchDialog = ({ open, onOpenChange, punchType, onSuccess }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [settings, setSettings] = useState(null);
  const [gps, setGps] = useState(null);          // {latitude, longitude, accuracy}
  const [gpsError, setGpsError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [photo, setPhoto] = useState(null);      // base64 data URL
  const [submitting, setSubmitting] = useState(false);
  const [hint, setHint] = useState(null);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraReady(false);
  }, []);

  const acquireGps = useCallback(() => {
    setGpsError(null);
    if (!('geolocation' in navigator)) {
      setGpsError('Your browser does not support GPS. Please use a modern browser over HTTPS.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => setGps({ latitude: p.coords.latitude, longitude: p.coords.longitude, accuracy: p.coords.accuracy }),
      (err) => {
        let msg = 'Could not get GPS location';
        if (err.code === err.PERMISSION_DENIED) msg = 'GPS permission denied. Please enable location access in your browser settings.';
        else if (err.code === err.POSITION_UNAVAILABLE) msg = 'GPS unavailable — are you indoors? Try going near a window.';
        else if (err.code === err.TIMEOUT) msg = 'GPS request timed out. Tap "Retry GPS" to try again.';
        setGpsError(msg);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('Camera is not available. Use a modern browser over HTTPS (camera/GPS require a secure context).');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraReady(true);
    } catch (e) {
      let msg = 'Could not access camera';
      if (e.name === 'NotAllowedError') msg = 'Camera permission denied. Please allow camera access and try again.';
      else if (e.name === 'NotFoundError') msg = 'No camera device found on this machine.';
      else if (e.name === 'NotReadableError') msg = 'Camera is in use by another application.';
      setCameraError(msg);
    }
  }, []);

  // On open: fetch settings + acquire permissions
  useEffect(() => {
    if (!open) return;
    setPhoto(null);
    setGps(null);
    setGpsError(null);
    setCameraError(null);
    setHint(null);
    (async () => {
      try {
        const r = await api.get('/me/attendance-settings');
        setSettings(r.data);
      } catch (_) {
        setSettings({ require_gps: true, require_photo: true });
      }
      acquireGps();
      startCamera();
    })();
    return stopCamera;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !cameraReady) return;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    // Overlay timestamp watermark for tamper-evidence
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillRect(0, canvas.height - 28, canvas.width, 28);
    ctx.fillStyle = '#fff';
    ctx.font = '14px monospace';
    ctx.fillText(new Date().toLocaleString(), 8, canvas.height - 9);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.75);
    setPhoto(dataUrl);
  };

  const retakePhoto = () => setPhoto(null);

  const submit = async () => {
    setHint(null);
    if (settings?.require_gps && !gps) { setHint('GPS reading is required — tap Retry GPS.'); return; }
    if (settings?.require_photo && !photo) { setHint('Photo is required — tap Capture Photo.'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('punch_type', punchType);
      fd.append('latitude', gps?.latitude ?? 0);
      fd.append('longitude', gps?.longitude ?? 0);
      if (gps?.accuracy != null) fd.append('accuracy', gps.accuracy);
      if (photo) fd.append('photo_base64', photo);
      const r = await api.post('/me/attendance/punch', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(punchType === 'in' ? 'Punched in!' : `Punched out — ${r.data.total_hours ?? ''} hrs`);
      stopCamera();
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Punch failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (o) => {
    if (!o) stopCamera();
    onOpenChange(o);
  };

  const isIn = punchType === 'in';
  const Icon = isIn ? LogIn : LogOut;
  const accuracyBadge = gps?.accuracy != null ? (
    <Badge variant={gps.accuracy < 50 ? 'default' : gps.accuracy < 200 ? 'secondary' : 'destructive'} className="ml-2">
      ±{Math.round(gps.accuracy)}m
    </Badge>
  ) : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md" data-testid="punch-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon size={18} />{isIn ? 'Punch In' : 'Punch Out'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {/* GPS panel */}
          <div className="border rounded-lg p-3 space-y-2" data-testid="gps-panel">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-medium">
                <MapPin size={16} className={gps ? 'text-emerald-600' : 'text-muted-foreground'} />
                Location {settings?.require_gps && <span className="text-red-500">*</span>}
                {gps && <CheckCircle2 size={14} className="text-emerald-600 ml-1" />}
                {accuracyBadge}
              </div>
              <Button size="sm" variant="outline" onClick={acquireGps} data-testid="retry-gps-btn"><RefreshCw size={12} className="mr-1" />Retry</Button>
            </div>
            {gps && (
              <p className="text-xs text-muted-foreground font-mono">
                {gps.latitude.toFixed(6)}, {gps.longitude.toFixed(6)}
              </p>
            )}
            {gpsError && (
              <div className="text-xs text-red-600 flex items-start gap-1" data-testid="gps-error">
                <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />{gpsError}
              </div>
            )}
            {!gps && !gpsError && (
              <p className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 size={12} className="animate-spin" />Acquiring GPS…</p>
            )}
          </div>

          {/* Camera / photo panel */}
          <div className="border rounded-lg p-3 space-y-2" data-testid="camera-panel">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Camera size={16} className={photo ? 'text-emerald-600' : 'text-muted-foreground'} />
                Live Photo {settings?.require_photo && <span className="text-red-500">*</span>}
                {photo && <CheckCircle2 size={14} className="text-emerald-600 ml-1" />}
              </div>
              {cameraReady && !photo && (
                <Button size="sm" onClick={capturePhoto} data-testid="capture-photo-btn"><Camera size={12} className="mr-1" />Capture</Button>
              )}
              {photo && (
                <Button size="sm" variant="outline" onClick={retakePhoto} data-testid="retake-photo-btn"><RefreshCw size={12} className="mr-1" />Retake</Button>
              )}
            </div>

            <div className="aspect-video bg-black rounded-md overflow-hidden relative flex items-center justify-center">
              {photo ? (
                <img src={photo} alt="captured" className="w-full h-full object-cover" data-testid="captured-photo" />
              ) : (
                <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="camera-preview" />
              )}
              {!cameraReady && !cameraError && !photo && (
                <div className="absolute inset-0 flex items-center justify-center text-white text-xs">
                  <Loader2 size={16} className="animate-spin mr-2" />Starting camera…
                </div>
              )}
            </div>
            <canvas ref={canvasRef} className="hidden" />

            {cameraError && (
              <div className="text-xs text-red-600 flex items-start gap-1" data-testid="camera-error">
                <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                <span>
                  {cameraError}{' '}
                  <button type="button" className="underline" onClick={startCamera}>Retry</button>
                </span>
              </div>
            )}
          </div>

          {hint && <p className="text-xs text-amber-600 flex items-center gap-1"><AlertTriangle size={12} />{hint}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} data-testid="punch-cancel-btn">Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting || (settings?.require_gps && !gps) || (settings?.require_photo && !photo)}
            data-testid="punch-submit-btn"
          >
            {submitting ? <Loader2 size={14} className="animate-spin mr-1" /> : <Icon size={14} className="mr-1" />}
            Confirm {isIn ? 'Punch In' : 'Punch Out'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PunchDialog;
